import { useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { useLanguage } from '../contexts/LanguageContext'

interface SEOHeadProps {
  title?: string
  description?: string
  image?: string
  noindex?: boolean
}

const SEOHead = ({ title, description, image, noindex = false }: SEOHeadProps) => {
  const location = useLocation()
  const { t } = useLanguage()

  useEffect(() => {
    const updateMetaTag = (property: string, content: string, isProperty = false) => {
      const selector = isProperty ? `meta[property="${property}"]` : `meta[name="${property}"]`
      let meta = document.querySelector(selector) as HTMLMetaElement
      
      if (!meta) {
        meta = document.createElement('meta')
        if (isProperty) {
          meta.setAttribute('property', property)
        } else {
          meta.setAttribute('name', property)
        }
        document.head.appendChild(meta)
      }
      meta.setAttribute('content', content)
    }

    const updateTitle = (newTitle: string) => {
      document.title = newTitle
      updateMetaTag('title', newTitle)
      updateMetaTag('og:title', newTitle, true)
      updateMetaTag('twitter:title', newTitle)
    }

    const updateDescription = (newDescription: string) => {
      updateMetaTag('description', newDescription)
      updateMetaTag('og:description', newDescription, true)
      updateMetaTag('twitter:description', newDescription)
    }

    const updateImage = (newImage: string) => {
      updateMetaTag('og:image', newImage, true)
      updateMetaTag('twitter:image', newImage)
    }

    const updateCanonical = (url: string) => {
      let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement
      if (!canonical) {
        canonical = document.createElement('link')
        canonical.setAttribute('rel', 'canonical')
        document.head.appendChild(canonical)
      }
      canonical.setAttribute('href', url)
    }

    // Determine page-specific SEO
    const path = location.pathname
    const baseUrl = 'https://juvenis.health'
    let pageTitle = title || 'Juvenis - Ваш цифровой наставник по долголетию'
    let pageDescription = description || t.hero.description || 'Мы превращаем твои данные и цели в план, чтобы жить дольше.'
    let pageImage = image || `${baseUrl}/logo.svg`
    let canonicalUrl = `${baseUrl}${path === '/' ? '' : path}`

    // Page-specific overrides
    if (path.includes('/waitlist')) {
      pageTitle = `${t.waitlist.title} ${t.waitlist.titleHighlight} ${t.waitlist.titleEnd} | Juvenis`
      pageDescription = `${t.waitlist.description} ${t.waitlist.descriptionHighlight} ${t.waitlist.descriptionEnd}`
    }

    // Update meta tags
    updateTitle(pageTitle)
    updateDescription(pageDescription)
    updateImage(pageImage)
    updateCanonical(canonicalUrl)

    // Robots meta
    if (noindex) {
      updateMetaTag('robots', 'noindex, nofollow')
    } else {
      updateMetaTag('robots', 'index, follow')
    }

    // Update Open Graph URL
    updateMetaTag('og:url', canonicalUrl, true)
    updateMetaTag('twitter:url', canonicalUrl)
  }, [location.pathname, title, description, image, noindex, t.hero.description, t.waitlist.title, t.waitlist.titleHighlight, t.waitlist.titleEnd, t.waitlist.description, t.waitlist.descriptionHighlight, t.waitlist.descriptionEnd])

  return null
}

export default SEOHead
